<template>
    <div component="VideoProjectItemVideo">
        <div class="videos">
            <div class="video" v-for="(item, index) in videos" :key="index">
                <div class="holder">
                    <div class="cell index">{{ index + 1 }}.</div>
                    <div class="cell name">{{ item.video_name }}</div>
                    <div class="cell date">{{ getDateWithTime(item.created_at) }}</div>
                </div>
                <btn
                    color="primary"
                    size="iconSmall"
                    :icon="'play_arrow'"
                    @click="play(item.hash)"
                    :state="!isPlayable(item) ? 'load' : ''"
                />
            </div>
        </div>
        <div class="buttons">
            <btn v-if="emailRequest" color="base" :icon="'radio_button_checked'" @click="record">
                Record new video
            </btn>
            <video-gallery-upload v-if="isAdmin" :uploading="[states.uploading].includes(state)" @submit="onUpload" />
        </div>
    </div>
</template>
<script>
import { mapMutations, mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import VideoGalleryUpload from '@/components/Profile/Video/Gallery/VideoGalleryUpload.vue';

const states = {
    idle: 'idle',
    saving: 'saving',
    saved: 'saved',
    uploading: 'uploading',
    deleting: 'deleting',
    error: 'error',
};

export default {
    name: 'VideoProjectItemVideo',

    props: {
        emailRequest: {
            type: Object,
        },
    },

    data() {
        return {
            states,
            state: states.idle,
            error: null,
            poll: null,
        };
    },

    created() {
        this.$_eventsSubscribe({ videoPortfolioItemCreated: 'startPolling' });
    },

    mounted() {
        if (this.hasUnprocessedVideos) {
            this.startPolling();
        }
    },

    beforeDestroy() {
        this.$_eventsUnsubscribe();
        this.stopPolling();
    },

    methods: {
        ...mapMutations(['setActorVideoPlaying']),
        ...mapActions(['storeVideoRequestVideo', 'loadActorVideoRequests']),

        getDateWithTime(date) {
            return `${moment(date).format('DD. MM. YYYY')}`;
        },

        play(hash) {
            this.setActorVideoPlaying({
                file: this.getVideoRequestVideoFileUrl(hash),
                controls: true,
                loop: false,
            });
        },

        record() {
            window.open('/video/request/' + this.emailRequest.hash);
        },

        async onUpload(file) {
            this.stopPolling();

            this.$_setState(states.uploading);

            try {
                const formData = new FormData();

                formData.append('video_hash', this.emailRequest.hash);
                formData.append('video', file, file.name);
                formData.append('uploaded', file.name);
                formData.append('avoidConvertio', !!this.isAdmin);

                await this.storeVideoRequestVideo(formData);
                await this.getFreshProjects();

                this.startPolling();
            } catch (e) {
                this.$_setState(states.error, states.idle);
                this.$env.log.error(e);
                this.$bugsnag.notify(e);
            } finally {
                this.$_setState(states.idle);
            }
        },

        async getFreshProjects() {
            return this.loadActorVideoRequests({
                include: ['project', 'project.client', 'emailRequests', 'emailRequests.videos', 'character'],
            });
        },

        startPolling() {
            const poll = async () => {
                if (!this.hasUnprocessedVideos) {
                    return;
                }

                await this.getFreshProjects();

                this.poll = setTimeout(poll, 5000);
            };

            if (this.hasUnprocessedVideos) {
                this.poll = setTimeout(poll, 1000);
            }
        },

        stopPolling() {
            if (this.poll) {
                clearTimeout(this.poll);
            }
        },
    },

    computed: {
        ...mapGetters(['getIsLoggedIn', 'hasRole', 'getUser', 'getVideoRequestVideoFileUrl']),

        isAdmin() {
            return (
                this.getIsLoggedIn && (this.hasRole('role_administrator') || this.getUser?.isImpersonated() || false)
            );
        },

        videos() {
            return this.emailRequest?.videos ?? [];
        },

        isPlayable() {
            return (video) => !!video.converted_at;
        },

        hasUnprocessedVideos() {
            return this.videos.some((i) => !i.converted_at);
        },
    },

    components: {
        VideoGalleryUpload,
    },
};
</script>
<style lang="less" scoped>
@import '~@variables';

[component='VideoProjectItemVideo'] {
    .video {
        .flex-row;
        padding: 5px 0;
        cursor: pointer;
        .border-b;

        .holder {
            .flex-row;

            @media (max-width: 460px) {
                .flex-column;
            }
        }

        .index {
            font-weight: 600;
            padding-right: 5px;
        }

        .name {
            flex: 1;
            text-overflow: ellipsis;
            overflow: hidden;
            width: 250px;
        }

        .date {
            padding: 0 10px;

            @media (max-width: 460px) {
                padding-left: 0;
            }
        }

        &:last-child {
            border-width: 0;
            margin-bottom: 5px;
        }
    }

    .buttons {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin: 10px 0 20px 0;

        @media (max-width: 460px) {
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            row-gap: 10px;
        }
    }
}
</style>
